/**
 * Reload once when a page's code file has gone missing.
 *
 * Pages are fetched the first time someone opens them (React.lazy in
 * App.jsx), from files whose names carry a hash of their contents. Every
 * deploy replaces them with new names. A tab opened before the deploy still
 * knows only the old names, so the first visit to a page it has not loaded yet
 * asks for a file that is no longer there. Vite reports that as a
 * `vite:preloadError` event on window.
 *
 * The answer is to reload, which fetches the new index and with it the new
 * names. Only once inside RELOAD_WINDOW_MS: if the reloaded page fails the
 * same way, the fault is something else, and reloading again would loop. Then
 * the error is left to reach the page's ErrorBoundary, which says so. When the
 * time of the last reload cannot be stored or read (storage blocked), it does
 * not reload at all, for the same reason.
 */

export const RELOAD_WINDOW_MS = 60_000;
const STORAGE_KEY = 'wph-chunk-reload-at';

export function installChunkReload({ win = window, storage, now = Date.now } = {}) {
  win.addEventListener('vite:preloadError', (event) => {
    try {
      /* Read inside the try: merely touching sessionStorage throws when the
         browser blocks site data. */
      const store = storage ?? win.sessionStorage;
      const last = Number(store.getItem(STORAGE_KEY) || 0);
      if (now() - last < RELOAD_WINDOW_MS) return;
      store.setItem(STORAGE_KEY, String(now()));
    } catch {
      return;
    }
    event.preventDefault();
    win.location.reload();
  });
}
